<mat-toolbar color="primary" class="app-toolbar">
  <button
    *ngIf="!isShareManagementPageOpen"
    mat-icon-button
    type="button"
    [attr.aria-label]="isAboutRoute || isListRoute || isSharePreviewPageOpen ? 'Back' : 'Toggle todo lists sidebar'"
    (click)="onToolbarNavAction()"
  >
    <mat-icon>{{ isAboutRoute || isListRoute || isSharePreviewPageOpen ? 'arrow_back' : 'menu' }}</mat-icon>
  </button>
  <span class="app-title">{{ toolbarTitle }}</span>
  <span class="spacer"></span>
  <button
    *ngIf="isShareManagementPageOpen"
    mat-icon-button
    type="button"
    aria-label="Close settings"
    (click)="closeShareManagementPage()"
  >
    <mat-icon>close</mat-icon>
  </button>
  <button
    *ngIf="isListRoute && !isShareManagementPageOpen && !isSharePreviewPageOpen"
    mat-icon-button
    type="button"
    aria-label="Manage rights and sharing"
    [disabled]="!activeList"
    (click)="openShareManagementPage()"
  >
    <mat-icon>edit</mat-icon>
  </button>
</mat-toolbar>

<div class="startup-overlay" *ngIf="isStartupLoading" aria-live="polite" aria-label="App is loading">
  <div class="startup-spinner-shell">
    <mat-progress-spinner class="startup-spinner" mode="indeterminate" [diameter]="62" [strokeWidth]="5"></mat-progress-spinner>
    <div class="startup-spinner-glow" aria-hidden="true"></div>
    <p>Loading Keeperoon...</p>
  </div>
</div>

<div class="sidebar-backdrop" *ngIf="isSidebarOpen" (click)="closeSidebar()"></div>

<aside class="sidebar-drawer" [class.sidebar-drawer-open]="isSidebarOpen" aria-label="Todo lists sidebar">
  <mat-card class="lists-card" appearance="raised">
    <mat-card-header class="profile-header">
      <div class="profile-avatar" *ngIf="hasProfilePhoto; else profileFallbackAvatar" (click)="onProfilePhotoClick()">
        <img [src]="profilePhotoUrl" alt="Profile photo" />
      </div>
      <ng-template #profileFallbackAvatar>
        <div class="profile-avatar profile-avatar-fallback" aria-hidden="true" (click)="onProfilePhotoClick()">
          <mat-icon>person</mat-icon>
        </div>
      </ng-template>
      <div class="profile-text">
        <div class="profile-label">Profile</div>
        <div class="profile-name">{{ profileName }}</div>
      </div>
    </mat-card-header>

    <mat-divider></mat-divider>

    <mat-card-content>
      <mat-list>
        <ng-container *ngFor="let list of todoLists; let isLast = last">
          <mat-list-item
            class="list-item"
            [class.list-item-active]="list.id === activeListId"
            (click)="selectList(list.id)"
            (keydown.enter)="selectList(list.id)"
            (keydown.space)="selectList(list.id); $event.preventDefault()"
            tabindex="0"
          >
            <div class="list-item-row">
              <div class="list-item-main">
                <div class="list-item-name">{{ list.name }}</div>
                <div class="list-item-sub">{{ roleLabel(list.currentUserRole) }} · by {{ list.creatorName }}</div>
              </div>
              <mat-chip *ngIf="list.pendingRequests > 0 && list.currentUserRole === 'owner'">{{ list.pendingRequests }}</mat-chip>
            </div>
          </mat-list-item>
          <mat-divider *ngIf="!isLast"></mat-divider>
        </ng-container>
      </mat-list>

      <mat-divider></mat-divider>

      <div class="new-list-row">
        <button mat-flat-button color="primary" (click)="openAddListPopup()">New list</button>
      </div>
    </mat-card-content>

    <mat-divider></mat-divider>

    <mat-card-actions align="end" class="drawer-actions">
      <button mat-button type="button" (click)="openAboutPage()">About</button>
    </mat-card-actions>
  </mat-card>
</aside>

<main class="page">
  <section class="layout-grid">
    <mat-card class="todo-card" appearance="raised" *ngIf="isSharePreviewPageOpen; else mainPageTpl">
      <mat-card-header>
        <mat-card-title>{{ sharePreview?.listName || 'Shared todo list' }}</mat-card-title>
        <mat-card-subtitle>Created by {{ sharePreview?.creatorName || 'Unknown' }}</mat-card-subtitle>
      </mat-card-header>

      <mat-card-content>
        <div *ngIf="isSharePreviewLoading" class="loading-row" aria-live="polite" aria-label="Loading shared link">
          <mat-progress-spinner class="loading-spinner" [diameter]="18" [strokeWidth]="3" mode="indeterminate"></mat-progress-spinner>
          <span class="loading-text">Loading shared list...</span>
        </div>

        <ng-container *ngIf="!isSharePreviewLoading && sharePreview">
          <p class="status-text" *ngIf="sharePreview.membershipStatus === 'pending'">Your request is pending owner approval.</p>
          <p class="status-text" *ngIf="sharePreview.membershipStatus === 'member' || sharePreview.membershipStatus === 'owner'">
            You already have access. Open lists to continue.
          </p>
        </ng-container>
      </mat-card-content>

      <mat-card-actions align="end">
        <button
          mat-flat-button
          color="primary"
          *ngIf="canJoinSharedList()"
          [disabled]="isJoinActionInFlight"
          (click)="requestToJoinSharedList()"
        >
          Join list
        </button>
        <button mat-button type="button" (click)="closeSharePreviewPage()">Close</button>
      </mat-card-actions>
    </mat-card>

    <ng-template #mainPageTpl>
      <mat-card class="todo-card" appearance="raised" *ngIf="isShareManagementPageOpen; else defaultPageTpl">
        <mat-card-header>
          <div class="todo-card-header-row">
            <mat-card-title>{{ activeListName }}</mat-card-title>
            <mat-chip color="primary" selected>{{ roleLabel(activeList?.currentUserRole || 'reader') }}</mat-chip>
          </div>
        </mat-card-header>

        <mat-card-content class="share-content">
          <section class="share-section">
            <h3>List settings</h3>
            <p class="share-description">Rename the list or remove it.</p>

            <form class="todo-form" (ngSubmit)="submitShareManagementListEdit()">
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>List name</mat-label>
                <input matInput name="renameListName" [(ngModel)]="renameListName" placeholder="List name" />
              </mat-form-field>

              <div class="edit-list-actions">
                <button mat-button color="warn" type="button" [disabled]="!isActiveListOwner || isEditListActionInFlight" (click)="removeActiveListFromShareManagement()">
                  Delete
                </button>
                <span class="spacer"></span>
                <button mat-flat-button color="primary" type="submit" [disabled]="!isActiveListOwner || isEditListActionInFlight">
                  Save
                </button>
              </div>
            </form>
          </section>

          <mat-divider></mat-divider>

          <section class="share-section">
            <h3>Notifications</h3>
            <p class="share-description">Get Telegram notifications for changes in this shared list.</p>

            <div class="share-buttons">
              <button
                mat-stroked-button
                type="button"
                [disabled]="!isActiveListShared || isNotificationPreferenceLoading || isNotificationPreferenceSaving"
                (click)="toggleActiveListNotifications()"
              >
                {{ isActiveListNotificationsEnabled ? 'Turn off notifications' : 'Turn on notifications' }}
              </button>
            </div>

            <p class="status-text" *ngIf="!isActiveListShared">Notifications are available after you create a shared link.</p>
          </section>

          <mat-divider></mat-divider>

          <div *ngIf="isLoadingShareData" class="loading-row" aria-live="polite" aria-label="Loading sharing settings">
            <mat-progress-spinner class="loading-spinner" [diameter]="18" [strokeWidth]="3" mode="indeterminate"></mat-progress-spinner>
            <span class="loading-text">Loading sharing settings...</span>
          </div>

          <section class="share-section">
            <h3>Shared link</h3>
            <p class="share-description">Create a link and send it to friends or contacts.</p>

            <div class="share-link-row" *ngIf="shareLink">
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="share-link-field">
                <mat-label>Share link</mat-label>
                <input matInput [value]="shareLink" readonly />
              </mat-form-field>
            </div>

            <div class="share-buttons">
              <button
                mat-flat-button
                color="primary"
                [disabled]="!isActiveListOwner || isCreatingShareLink || !!activeList?.shareToken"
                (click)="createShareLink()"
              >
                Create link
              </button>
              <button mat-button type="button" [disabled]="!shareLink" (click)="copyShareLink()">Copy</button>
              <button mat-button type="button" [disabled]="!shareLink" (click)="shareByNativeDialog()">Send</button>
            </div>
          </section>

          <mat-divider></mat-divider>

          <section class="share-section" *ngIf="isActiveListOwner; else membersReadonlyTpl">
            <h3>Members</h3>
            <p class="share-description">Set access level: read only, writer, or owner.</p>

            <div class="member-list" *ngIf="shareMembers.length; else noMembersTpl">
              <div class="member-item" *ngFor="let member of shareMembers">
                <div class="member-info">
                  <div class="member-name">{{ member.userName }}</div>
                  <div class="member-role-label">Current: {{ roleLabel(member.role) }}</div>
                  <div class="member-role-label">Notifications: {{ member.notificationsEnabled ? 'On' : 'Off' }}</div>
                </div>
                <div class="member-role-buttons">
                  <button mat-stroked-button type="button" [disabled]="isProcessingShareAction" (click)="changeMemberRole(member, 'reader')">Read</button>
                  <button mat-stroked-button type="button" [disabled]="isProcessingShareAction" (click)="changeMemberRole(member, 'writer')">Write</button>
                  <button mat-stroked-button type="button" [disabled]="isProcessingShareAction || member.isSelf" (click)="changeMemberRole(member, 'owner')">Owner</button>
                </div>
              </div>
            </div>
            <ng-template #noMembersTpl>
              <p class="status-text">No members yet.</p>
            </ng-template>
          </section>
          <ng-template #membersReadonlyTpl>
            <section class="share-section">
              <h3>Members</h3>
              <p class="status-text">Only owner can manage member roles.</p>
            </section>
          </ng-template>

          <mat-divider></mat-divider>

          <section class="share-section" *ngIf="isActiveListOwner; else requestsReadonlyTpl">
            <h3>Permission requests</h3>
            <p class="share-description">Approve or reject users who asked to join.</p>

            <div class="request-list" *ngIf="permissionRequests.length; else noRequestsTpl">
              <div class="request-item" *ngFor="let request of permissionRequests">
                <div class="request-info">
                  <div class="request-name">{{ request.userName }}</div>
                  <div class="request-date" *ngIf="request.requestedAt">Requested: {{ request.requestedAt | date: 'short' }}</div>
                </div>
                <div class="request-actions">
                  <button mat-flat-button color="primary" type="button" [disabled]="isProcessingShareAction" (click)="approvePermissionRequest(request)">
                    Accept
                  </button>
                  <button mat-button color="warn" type="button" [disabled]="isProcessingShareAction" (click)="rejectPermissionRequest(request)">
                    Reject
                  </button>
                </div>
              </div>
            </div>
            <ng-template #noRequestsTpl>
              <p class="status-text">No pending requests.</p>
            </ng-template>
          </section>
          <ng-template #requestsReadonlyTpl>
            <section class="share-section">
              <h3>Permission requests</h3>
              <p class="status-text">Only owner can review join requests.</p>
            </section>
          </ng-template>
        </mat-card-content>

        <mat-card-actions align="end">
          <button mat-button type="button" (click)="closeShareManagementPage()">Back to list</button>
        </mat-card-actions>
      </mat-card>

      <ng-template #defaultPageTpl>
        <app-main-page
          *ngIf="isMainRoute"
          [todoLists]="todoLists"
          [activeListId]="activeListId"
          (selectList)="selectList($event)"
        ></app-main-page>

        <app-todo-list-page
          *ngIf="isListRoute"
          [listId]="activeListId"
          [listName]="activeListName"
          [remainingCount]="remainingCount"
          [canWrite]="canWriteActiveList"
          [isLoading]="isLoading"
          [blocks]="todoBlocks"
          (toggleTodo)="toggleTodo($event)"
          (removeTodo)="removeTodo($event)"
          (renameTodo)="renameTodo($event.todo, $event.title)"
          (reorderBlocks)="onBlocksReordered($event)"
          (createSection)="createSection($event)"
          (renameSection)="renameSection($event.sectionId, $event.title)"
          (deleteSection)="deleteSection($event)"
        ></app-todo-list-page>

        <app-about-page
          *ngIf="isAboutRoute"
          [buildVersion]="buildVersion"
          [backendVersion]="backendVersion"
          [debugVisible]="isDebugInfoVisible"
          [isTelegramWebApp]="isTelegramWebApp"
          [hasTelegramInitData]="hasTelegramInitData"
          [telegramVerifyState]="telegramVerifyState"
          [telegramVerifyMessage]="telegramVerifyMessage"
          [telegramInitDataRaw]="telegramInitDataRaw"
          [telegramInitDataEntries]="telegramInitDataEntries"
          [telegramInitDataUnsafeDebug]="telegramInitDataUnsafeDebug"
          [telegramVerifyResponseDebug]="telegramVerifyResponseDebug"
          [telegramVerifyErrorDebug]="telegramVerifyErrorDebug"
          [debugLocationHref]="debugLocationHref"
          [debugLocationSearch]="debugLocationSearch"
          [debugLocationHash]="debugLocationHash"
        ></app-about-page>
      </ng-template>
    </ng-template>
  </section>
</main>

<div
  class="add-todo-backdrop"
  *ngIf="isAddTodoPopupOpen || isAddListPopupOpen"
  (click)="closeAddTodoPopup(); closeAddListPopup()"
></div>

<section class="add-todo-popup" *ngIf="isAddTodoPopupOpen" aria-label="Add item popup">
  <mat-card appearance="raised" class="add-todo-popup-card">
    <button
      mat-icon-button
      class="popup-close-button"
      type="button"
      aria-label="Close add task popup"
      [disabled]="isCreateTodoActionInFlight"
      (click)="closeAddTodoPopup()"
    >
      <mat-icon>close</mat-icon>
    </button>

    <mat-card-header>
      <mat-card-title>{{ isCreateSectionMode ? 'Add section' : 'Add task' }}</mat-card-title>
      <mat-card-subtitle>
        {{ isCreateSectionMode ? 'Create a new section for your list.' : 'Create a new item for your list.' }}
      </mat-card-subtitle>
    </mat-card-header>

    <mat-card-content>
      <form class="todo-form" (ngSubmit)="addTodo()">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>{{ isCreateSectionMode ? 'Section title' : 'Add a task' }}</mat-label>
          <input
            matInput
            name="newTodo"
            [(ngModel)]="newTodo"
            placeholder="e.g. Finalize mobile design"
            [disabled]="isCreateTodoActionInFlight"
            autofocus
          />
        </mat-form-field>

        <mat-checkbox [(ngModel)]="isCreateSectionMode" name="createSectionMode" [disabled]="isCreateTodoActionInFlight">
          Create section instead of item
        </mat-checkbox>

        <div class="popup-actions">
          <button mat-button type="button" [disabled]="isCreateTodoActionInFlight" (click)="closeAddTodoPopup()">Cancel</button>
          <button mat-flat-button color="primary" type="submit" [disabled]="isCreateTodoActionInFlight || !newTodo.trim()">
            <ng-container *ngIf="isCreateTodoActionInFlight; else addTodoActionLabel">
              <span class="action-with-spinner">
                <mat-progress-spinner class="loading-spinner" [diameter]="18" [strokeWidth]="3" mode="indeterminate"></mat-progress-spinner>
                Adding...
              </span>
            </ng-container>
            <ng-template #addTodoActionLabel>{{ isCreateSectionMode ? 'Add section' : 'Add' }}</ng-template>
          </button>
        </div>
      </form>
    </mat-card-content>
  </mat-card>
</section>

<section class="add-todo-popup" *ngIf="isAddListPopupOpen" aria-label="Add list popup">
  <mat-card appearance="raised" class="add-todo-popup-card">
    <button
      mat-icon-button
      class="popup-close-button"
      type="button"
      aria-label="Close add list popup"
      (click)="closeAddListPopup()"
    >
      <mat-icon>close</mat-icon>
    </button>

    <mat-card-header>
      <mat-card-title>New list</mat-card-title>
      <mat-card-subtitle>Create a new todo list.</mat-card-subtitle>
    </mat-card-header>

    <mat-card-content>
      <form class="todo-form" (ngSubmit)="createTodoList()">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>List name</mat-label>
          <input
            matInput
            name="newListName"
            [(ngModel)]="newListName"
            placeholder="e.g. Work"
          />
        </mat-form-field>

        <div class="popup-actions">
          <button mat-button type="button" (click)="closeAddListPopup()">Cancel</button>
          <button
            mat-flat-button
            color="primary"
            type="button"
            (pointerdown)="createTodoList(); $event.preventDefault(); $event.stopPropagation()"
            (click)="createTodoList()"
          >
            Create
          </button>
        </div>
      </form>
    </mat-card-content>
  </mat-card>
</section>

<button
  *ngIf="isListRoute && !isShareManagementPageOpen && !isSharePreviewPageOpen"
  mat-fab
  color="primary"
  class="add-todo-fab"
  aria-label="Add task"
  [disabled]="!canWriteActiveList"
  (click)="openAddTodoPopup()"
>
  <mat-icon>add</mat-icon>
</button>
